import { Form, Input, InputNumber, Select, Switch } from "antd"
import { toastError, toastSuccess } from "../../utils/toast"
import ButtonBack from "../../components/button/ButtonBack"
import { ICreateCompany } from "../../models/company.modal"
import ButtonSubmitBack from "../../components/button/ButtonSubmitBack"
import { useLoading } from "../../context/LoadingContext"
import { apiCreateCompany, apiGetListPackages } from "../../api"
import { useCallback, useEffect, useState } from "react"
import { IPackage } from "../../models/package.modal"
import { formatCurrency } from "../../utils/string-utils"
import defaultParams from "../../assets/constants/default_params"
import { filterOptionByLabel } from "../../utils/filter-utils"

export default function CompanyCreate() {
    const [packageOptions, setPackageOptions] = useState<{ label: string, value: Number }[]>([])
    const [formCreate] = Form.useForm()
    const { setIsLoading } = useLoading()

    const fetchPackages = useCallback(async () => {
        try {
            setIsLoading(true)
            const resPackages = await apiGetListPackages({ limit: defaultParams.MAX_LIMIT })
            if (resPackages.data != null) {
                const options = resPackages.data.packages.map((item: IPackage) => ({
                    label: `${item.id} - ${item.name} - ${formatCurrency(Number(item.price))}/${item.numberDay} ngày`,
                    value: item.id
                }))
                setPackageOptions(options)
            } else {
                setPackageOptions([])
            }
            setIsLoading(false)
        } catch (error) {
            setIsLoading(false)
            console.log(error)
        }
    }, [setIsLoading])

    useEffect(() => {
        fetchPackages()
    }, [fetchPackages])

    const handleCreate = async (values: any) => {
        try {
            setIsLoading(true)
            const dataToSubmit: ICreateCompany = {
                name: values.name || '',
                code: values.code || '',
                templateId: values.templateId || '',
                packageNewId: values.packageNewId || 0,
                supportTelesale: values.supportTelesale || false,
                hasOutlets: values.hasOutlets ?? false,
                description: values.description || '',
                supportChannels: []
            }
            if (values.supportApp) {
                dataToSubmit.supportChannels?.push('APP')
            }
            if (values.supportTelegram) {
                dataToSubmit.supportChannels?.push('TELEGRAM')
            }
            if (values.supportSlack) {
                dataToSubmit.supportChannels?.push('SLACK')
            }
            if (values.supportZalo) {
                dataToSubmit.supportChannels?.push('ZALO')
            }
            if (values.supportInventory) {
                dataToSubmit.supportChannels?.push('INVENTORY')
            }
            if (values.supportWarehouse) {
                dataToSubmit.supportChannels?.push('WAREHOUSE')
            }
            if (values.supportSalesInventory) {
                dataToSubmit.supportChannels?.push('SALES_INVENTORY')
            }
            dataToSubmit.costRateConnection = (values.costRateConnectionPercent ?? 0) / 100
            dataToSubmit.costRateExtension = (values.costRateExtensionPercent ?? 0) / 100
            const resp = await apiCreateCompany(dataToSubmit)
            if (resp.status === 200) {
                toastSuccess('Thêm công ty mới thành công!')
                formCreate.resetFields()
            } else {
                toastError('Có lỗi xảy ra!')
            }
            setIsLoading(false)
        } catch (error) {
            console.log(error);
            toastError('Có lỗi xảy ra!')
            setIsLoading(false)
        }
    }

    return (
        <div>
            <ButtonBack />
            <Form
                form={formCreate}
                onFinish={handleCreate}
                layout="vertical"
                initialValues={{
                    supportChannels: {
                        APP: true,
                        SLACK: true,
                        TELEGRAM: false,
                        ZALO: false
                    }
                }}
            >
                <Form.Item name='name' label='Tên công ty'
                    rules={[{ required: true, message: 'Vui lòng nhập tên công ty!' }]}>
                    <Input />
                </Form.Item>
                <Form.Item name='code' label='Mã công ty'
                    rules={[{ required: true, message: 'Vui lòng nhập mã công ty!' }]}>
                    <Input />
                </Form.Item>
                <Form.Item name='templateId' label='ID Template'>
                    <Input />
                </Form.Item>
                <Form.Item name='packageNewId' label='Gói cước gợi ý'>
                    <Select
                        style={{ width: '100%' }}
                        showSearch
                        placeholder="Chọn gói cước"
                        options={packageOptions}
                        filterOption={filterOptionByLabel}
                    />
                </Form.Item>
                <Form.Item label='Kênh hỗ trợ'>
                    <div style={{ display: 'flex', gap: '16px' }}>
                        <Form.Item name="supportTelesale" valuePropName="checked" noStyle>
                            <Switch checkedChildren="TELESALE" unCheckedChildren="TELESALE" />
                        </Form.Item>
                        <Form.Item name="supportApp" valuePropName="checked" noStyle>
                            <Switch checkedChildren="APP" unCheckedChildren="APP" />
                        </Form.Item>
                        <Form.Item name="supportTelegram" valuePropName="checked" noStyle>
                            <Switch checkedChildren="TELEGRAM" unCheckedChildren="TELEGRAM" />
                        </Form.Item>
                        <Form.Item name="supportSlack" valuePropName="checked" noStyle>
                            <Switch checkedChildren="SLACK" unCheckedChildren="SLACK" />
                        </Form.Item>
                        <Form.Item name="supportZalo" valuePropName="checked" noStyle>
                            <Switch checkedChildren="ZALO" unCheckedChildren="ZALO" />
                        </Form.Item>
                        <Form.Item name="supportInventory" valuePropName="checked" noStyle>
                            <Switch checkedChildren="INVENTORY" unCheckedChildren="INVENTORY" />
                        </Form.Item>
                        <Form.Item name="supportWarehouse" valuePropName="checked" noStyle>
                            <Switch checkedChildren="WAREHOUSE" unCheckedChildren="WAREHOUSE" />
                        </Form.Item>
                        <Form.Item name="supportSalesInventory" valuePropName="checked" noStyle>
                            <Switch checkedChildren="SALES_INVENTORY" unCheckedChildren="SALES_INVENTORY" />
                        </Form.Item>
                    </div>
                </Form.Item>
                <Form.Item
                    name='hasOutlets'
                    label='Có đại lý con'
                    valuePropName='checked'
                    initialValue={false}
                >
                    <Switch checkedChildren='Có' unCheckedChildren='Không' />
                </Form.Item>
                <Form.Item
                    name='costRateConnectionPercent'
                    label='Tỷ lệ chi phí đấu mới (%)'
                    tooltip='Tỷ lệ chi phí trên doanh thu đấu nối mới. Mặc định 0 = không áp dụng chi phí.'
                    rules={[
                        { type: 'number', min: 0, max: 100, message: 'Tỷ lệ phải nằm trong khoảng 0-100%!' },
                    ]}
                >
                    <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.01} precision={2} addonAfter='%' />
                </Form.Item>
                <Form.Item
                    name='costRateExtensionPercent'
                    label='Tỷ lệ chi phí gia hạn (%)'
                    tooltip='Tỷ lệ chi phí trên doanh thu gia hạn. Mặc định 0 = không áp dụng chi phí.'
                    rules={[
                        { type: 'number', min: 0, max: 100, message: 'Tỷ lệ phải nằm trong khoảng 0-100%!' },
                    ]}
                >
                    <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.01} precision={2} addonAfter='%' />
                </Form.Item>
                <Form.Item name='description' label='Mô tả'>
                    <Input />
                </Form.Item>
                <ButtonSubmitBack />
            </Form>
        </div>
    )
}
